import {
    BandKind, ObjKind, HAlign, VAlign, FRAME_BOTTOM, FRAME_LEFT, FRAME_RIGHT, FRAME_TOP,
    MM_TO_POINTS, UNITS_TO_POINTS, firstPage, fontSizePoints, isBold, isInherited, isItalic,
    isUnderline, pageHeightPoints, pageWidthPoints,
} from './fr3Doc';
import { DrawKind, fillOp, frameOp, imageOp, newDrawOp } from './fr3DrawOp';
import { Fr3Expr, fieldName, num as exprNum, truth as exprTruth } from './fr3Expr';
import { Fr3Format } from './fr3Format';
import { Fr3Script } from './fr3Script';
import * as Fr3Barcode from './fr3Barcode';
import { fieldsOf, formatDateFields, isWhitespace, javaTrim } from './javaCompat';

/**
 * Runs a parsed template against a row set and produces positioned pages
 * (port of Fr3Engine).
 *
 * Band order follows FastReport 4 for a single report page: the report title
 * once (before the page header), the page header and column header on every
 * page, the data header once before the data (again on a new page with
 * ReprintOnNewPage), any number of nested groups around the master data, the
 * data footer, the report summary, and the page footer / column footer
 * pinned to the bottom margin. A band's Child band follows it.
 *
 * The Java engine on the tablet is the reference; both are checked against the
 * golden fixtures in MyFasManMobile tools/fr3harness/golden.
 */

/** Memo text inset FastReport applies (2 px horizontally, 1 px vertically). */
export const GAP_X_UNITS = 2;
export const GAP_Y_UNITS = 1;
/**
 * Stretch estimate without font metrics: average glyph advance as a fraction
 * of the font size (Arial), and line height as a multiple of it. Both engines
 * use these constants, so the layout is reproducible.
 */
export const AVG_CHAR = 0.5;
export const AVG_CHAR_BOLD = 0.55;
export const LINE_HEIGHT = 1.2;

/**
 * Options: { logo: Uint8Array|null, images: Map(lower-case field -> bytes),
 * format: Fr3Format, variables: Map, maxPages: 200, now: Date|null }.
 */
export const newOptions = () => ({
    /** The client logo (ticket logo; the invoice logo for invoices). */
    logo: null,
    /** Decoded image columns, keyed by lower-case field name. */
    images: new Map(),
    format: new Fr3Format(),
    /**
     * Report variables such as RepTitle, exposed to expressions. Values are plain
     * text (not expressions), names match case-insensitively and win over the
     * template's own Variables (also inside <inherited> layouts). The host set
     * MySales/MyFashionManager always fills - RepTitle, RepSelection,
     * RepLicenceName, VarBetm1-30, VarCode1-20Oms, VarLevart1-5Oms,
     * varTicketHeader1-7, varTicketFooter1-20 - is "" when not passed here.
     */
    variables: new Map(),
    /** Hard stop so a malformed template cannot spool forever. */
    maxPages: 200,
    /** [Date] / [Time]; null = the moment of rendering. Fixed in golden tests. */
    now: null,
});

const newObjState = (o) => ({
    left: o.left,
    top: o.top,
    width: o.width,
    height: o.height,
    visible: o.visible,
    textOverride: null,
    loadLogo: false,
});

/** Copy of an object state (Java ObjState(ObjState)). */
const copyObjState = (s) => ({
    left: s.left,
    top: s.top,
    width: s.width,
    height: s.height,
    visible: s.visible,
    textOverride: s.textOverride,
    loadLogo: s.loadLogo,
});

/** Grows with its own text (smActualHeight). */
const isActualStretch = (o) => o.stretchMode === 'smActualHeight';

/** Stretches to the bottom of the tallest object of the stretched band (smMaxHeight). */
const isMaxStretch = (o) => o.stretchMode === 'smMaxHeight';

/**
 * Height the memo text needs, in fr3 units, from the shared glyph-width
 * estimate: hard lines plus estimated wraps, times the line height, plus
 * FastReport's vertical inset.
 */
export const textHeightUnits = (o, st, text) => {
    if (text == null || text === '') return 0;
    const fontPx = Math.abs(o.fontHeight) <= 0 ? 13 : Math.abs(o.fontHeight);
    const charW = fontPx * (isBold(o) ? AVG_CHAR_BOLD : AVG_CHAR);
    const avail = Math.max(1, st.width - 2 * GAP_X_UNITS);
    let lines = 0;
    for (const hard of text.split(/\r\n|\n|\r/)) {
        if (!o.wordWrap) {
            lines++;
            continue;
        }
        const wpx = hard.length * charW;
        lines += Math.max(1, Math.trunc(Math.ceil(wpx / avail)));
    }
    return lines * fontPx * LINE_HEIGHT + 2 * GAP_Y_UNITS;
};

/**
 * AllowHTMLTags memo text: FastReport's tag set (b, i, u, br, font, sub, sup)
 * reduced to plain text plus whole-text styles.
 */
/** Java's [\s/]: \s is only [ \t\n\x0B\f\r] in java.util.regex. */
const JAVA_WS_OR_SLASH = new RegExp('[ \t\n\u000B\f\r/]');

export const parseHtmlText = (s) => {
    const r = { text: '', allBold: false, allItalic: false, allUnderline: false };
    let out = '';
    let b = 0;
    let i = 0;
    let u = 0;
    let anyVisible = false;
    let everNotBold = false;
    let everNotItalic = false;
    let everNotUnderline = false;
    let k = 0;
    while (k < s.length) {
        const c = s[k];
        if (c === '<') {
            const close = s.indexOf('>', k + 1);
            if (close > k) {
                const tag = javaTrim(s.substring(k + 1, close)).toLowerCase();
                const end = tag.startsWith('/');
                const name = (end ? tag.substring(1) : tag).split(JAVA_WS_OR_SLASH)[0];
                switch (name) {
                    case 'b': b += end ? -1 : 1; break;
                    case 'i': i += end ? -1 : 1; break;
                    case 'u': u += end ? -1 : 1; break;
                    case 'br': out += '\n'; break;
                    default: break;
                }
                k = close + 1;
                continue;
            }
        }
        if (c === '&') {
            const semi = s.indexOf(';', k);
            if (semi > k && semi - k <= 6) {
                const ent = s.substring(k + 1, semi);
                const rep = ent === 'amp' ? '&' : ent === 'lt' ? '<' : ent === 'gt' ? '>'
                    : ent === 'nbsp' ? ' ' : ent === 'quot' ? '"' : null;
                if (rep != null) {
                    out += rep;
                    k = semi + 1;
                    continue;
                }
            }
        }
        out += c;
        if (!isWhitespace(c)) {
            anyVisible = true;
            if (b <= 0) everNotBold = true;
            if (i <= 0) everNotItalic = true;
            if (u <= 0) everNotUnderline = true;
        }
        k++;
    }
    r.text = out;
    r.allBold = anyVisible && !everNotBold;
    r.allItalic = anyVisible && !everNotItalic;
    r.allUnderline = anyVisible && !everNotUnderline;
    return r;
};

const HOST_VARIABLE = /^(?:reptitle|repselection|replicencename|varbetm\d+|varcode\d+oms|varlevart\d+oms|varticketheader\d+|varticketfooter\d+)$/i;

/** A variable the host program always sets at print time (empty when the caller passes none). */
export const isHostVariable = (name) => name != null && HOST_VARIABLE.test(javaTrim(name));

export class Fr3Engine {
    constructor(doc, data, options) {
        this.doc = doc;
        this.data = data;
        this.options = options == null ? newOptions() : options;
        if (!this.options.images) this.options.images = new Map();
        if (!this.options.variables) this.options.variables = new Map();
        if (this.options.maxPages == null) this.options.maxPages = 200;
        this.format = this.options.format == null ? new Fr3Format() : this.options.format;
        this.expr = new Fr3Expr(this.format);
        this.script = new Fr3Script(doc.scriptText);
        /** Non-fatal issues worth surfacing before trusting the output. */
        this.warnings = [];

        this.scriptVars = new Map();
        for (const v of this.script.declaredVariables()) this.scriptVars.set(v.toLowerCase(), 0);
        this.objState = new Map();
        this.objByName = new Map();
        this.bandByName = new Map();

        this.currentRow = 0;
        this.groupStart = 0;
        this.groupEnd = 0;
        this.currentPageNo = 0;
        /** Line#: 1-based record number inside the innermost group. */
        this.lineNo = 0;
        /** -1 until the counting pass has run; see render(). */
        this.totalPages = -1;
        this.docVarCache = new Map();

        const page = firstPage(doc);
        if (page != null) {
            for (const b of page.bands) {
                if (b.name != null && b.name !== '') this.bandByName.set(b.name, b);
                for (const o of b.objects) {
                    if (o.name != null && o.name !== '') this.objByName.set(o.name, o);
                }
            }
        }

        this.context = this.makeContext();
        this.access = this.makeAccess();
    }

    /**
     * Lays out twice: TotalPages# cannot be known until the flow has
     * finished, so the first pass only counts pages and the second renders.
     */
    render() {
        const counting = this.layout();
        this.totalPages = counting.length;
        this.warnings.length = 0;
        return this.layout();
    }

    layout() {
        this.currentPageNo = 0;
        this.lineNo = 0;
        // The counting pass left these on the last row; the page header of the
        // first page must see the first one.
        this.currentRow = 0;
        this.groupStart = 0;
        this.groupEnd = this.data.rowCount() - 1;
        this.objState.clear();
        this.scriptVars.clear();
        for (const v of this.script.declaredVariables()) this.scriptVars.set(v.toLowerCase(), 0);
        this.doc.warnings.forEach((w) => this.warnings.push(w));
        const pages = [];
        const page = firstPage(this.doc);
        if (page == null) return pages;

        if (isInherited(this.doc)) {
            this.warn('template inherits from ' + this.doc.parentReport + '; parent not merged');
        }

        const left = page.leftMarginMm * MM_TO_POINTS;
        const top = page.topMarginMm * MM_TO_POINTS;
        const contentBottom = pageHeightPoints(page) - page.bottomMarginMm * MM_TO_POINTS;

        const title = this.band(page, BandKind.REPORT_TITLE);
        const pageHeader = this.band(page, BandKind.PAGE_HEADER);
        const pageFooter = this.band(page, BandKind.PAGE_FOOTER);
        const columnHeader = this.band(page, BandKind.COLUMN_HEADER);
        const columnFooter = this.band(page, BandKind.COLUMN_FOOTER);
        const overlay = this.band(page, BandKind.OVERLAY);
        const header = this.band(page, BandKind.HEADER);
        const footer = this.band(page, BandKind.FOOTER);
        const masterData = this.band(page, BandKind.MASTER_DATA);
        const summary = this.band(page, BandKind.REPORT_SUMMARY);
        const groupHeaders = this.bands(page, BandKind.GROUP_HEADER);
        const groupFooters = this.groupFootersByLevel(page, groupHeaders.length);
        if (this.band(page, BandKind.DETAIL_DATA) != null) {
            this.warn('detail data band not rendered (the data is a single flat row set)');
        }

        const footerHeight = pageFooter == null || !pageFooter.visible
            ? 0 : pageFooter.height * UNITS_TO_POINTS;
        const columnFooterHeight = columnFooter == null || !columnFooter.visible
            ? 0 : columnFooter.height * UNITS_TO_POINTS;
        const flowBottom = contentBottom - footerHeight - columnFooterHeight;

        const cur = new Cursor(this, pages, page, left, top, flowBottom, pageHeader, columnHeader);
        cur.start(title);

        if (header != null) {
            this.currentRow = 0;
            this.groupStart = 0;
            this.groupEnd = this.data.rowCount() - 1;
            cur.place(header);
            if (header.reprintOnNewPage) cur.reprint.push(header);
        }
        this.emitGroups(cur, groupHeaders, groupFooters, masterData, 0, 0, this.data.rowCount() - 1);
        if (header != null) removeFirst(cur.reprint, header);
        if (footer != null) {
            this.currentRow = Math.max(0, this.data.rowCount() - 1);
            this.groupStart = 0;
            this.groupEnd = this.data.rowCount() - 1;
            cur.place(footer);
        }

        if (summary != null) {
            this.currentRow = Math.max(0, this.data.rowCount() - 1);
            this.groupStart = 0;
            this.groupEnd = this.data.rowCount() - 1;
            cur.place(summary);
        }

        cur.pageStates.push(this.capturePage());
        for (let i = 0; i < pages.length; i++) {
            this.currentPageNo = i + 1;
            if (i < cur.pageStates.length) this.restorePage(cur.pageStates[i]);
            if (columnFooter != null && columnFooter.visible) {
                this.emitBandInto(pages[i], columnFooter, left, flowBottom);
            }
            if (pageFooter != null && pageFooter.visible) {
                this.emitBandInto(pages[i], pageFooter, left, contentBottom - footerHeight);
            }
            if (overlay != null && overlay.visible) {
                this.emitBandInto(pages[i], overlay, left, top);
            }
        }
        return pages;
    }

    /**
     * One nesting level: split [from, to] on this level's group condition and
     * print header / inner level / footer per group. Below the last level the
     * master data band iterates the rows.
     */
    emitGroups(cur, headers, footers, masterData, level, from, to) {
        if (level >= headers.length) {
            if (masterData == null || !masterData.visible) return;
            for (let r = from; r <= to && r < this.data.rowCount(); r++) {
                this.currentRow = r;
                this.groupStart = from;
                this.groupEnd = to;
                this.lineNo++;
                cur.place(masterData);
            }
            return;
        }
        const gh = headers[level];
        const gf = level < footers.length ? footers[level] : null;
        for (const g of this.groupRanges(gh, from, to)) {
            this.currentRow = g[0];
            this.groupStart = g[0];
            this.groupEnd = g[1];
            if (level === headers.length - 1) this.lineNo = 0;
            if (gh.visible) {
                if (gh.startNewPage) cur.breakPage();
                cur.place(gh);
            }
            if (gh.reprintOnNewPage) cur.reprint.push(gh);
            this.emitGroups(cur, headers, footers, masterData, level + 1, g[0], g[1]);
            removeFirst(cur.reprint, gh);
            this.currentRow = Math.max(g[0], g[1]);
            this.groupStart = g[0];
            this.groupEnd = g[1];
            if (gf != null && gf.visible) cur.place(gf);
        }
    }

    /**
     * What a page footer sees: FastReport prints it when its page is full, so
     * with the object, script and record state of that moment - not the state
     * at the end of the report.
     */
    capturePage() {
        const objs = new Map();
        this.objState.forEach((v, k) => objs.set(k, copyObjState(v)));
        return {
            objs,
            vars: new Map(this.scriptVars),
            variables: new Map(this.options.variables),
            row: this.currentRow,
            from: this.groupStart,
            to: this.groupEnd,
            line: this.lineNo,
        };
    }

    restorePage(ps) {
        this.objState.clear();
        ps.objs.forEach((v, k) => this.objState.set(k, copyObjState(v)));
        this.scriptVars.clear();
        ps.vars.forEach((v, k) => this.scriptVars.set(k, v));
        this.options.variables.clear();
        ps.variables.forEach((v, k) => this.options.variables.set(k, v));
        this.currentRow = ps.row;
        this.groupStart = ps.from;
        this.groupEnd = ps.to;
        this.lineNo = ps.line;
    }

    /** Height a band will take, after its script and stretching; nothing is emitted. */
    measure(b) {
        const scratch = { widthPoints: 0, heightPoints: 0, ops: [] };
        const warn = this.warnings.length;
        // The band script runs while measuring; scripts accumulate, so the
        // measuring run must leave no trace.
        const savedScriptVars = new Map(this.scriptVars);
        const savedVariables = new Map(this.options.variables);
        // Object state persists across bands (see runBandScript), so the
        // measuring run's property writes are rolled back as well.
        const savedObjState = new Map();
        this.objState.forEach((v, k) => savedObjState.set(k, copyObjState(v)));
        const h = this.emitBandInto(scratch, b, 0, 0);
        this.scriptVars.clear();
        savedScriptVars.forEach((v, k) => this.scriptVars.set(k, v));
        this.options.variables.clear();
        savedVariables.forEach((v, k) => this.options.variables.set(k, v));
        this.objState.clear();
        savedObjState.forEach((v, k) => this.objState.set(k, v));
        // A measuring pass must not double the warnings of the real one.
        while (this.warnings.length > warn) this.warnings.pop();
        return h;
    }

    emitBandInto(target, b, originX, originY) {
        // FastReport TfrxReportComponent/TfrxCustomMemoView BeforePrint and
        // AfterPrint: the band's own objects save their bounds and memo text
        // before the band's OnBeforePrint and get them back after the band is
        // printed, so a footer script that only shifts (Top := Top - 30)
        // starts from the design again for the next group. Writes to objects
        // of OTHER bands, and Visible, persist.
        const own = new Map();
        for (const o of b.objects) own.set(o.name, copyObjState(this.state(o)));
        try {
            return this.emitBandBody(target, b, originX, originY);
        } finally {
            for (const o of b.objects) {
                const saved = own.get(o.name);
                const st = this.objState.get(o.name);
                if (saved != null && st != null) {
                    st.left = saved.left;
                    st.top = saved.top;
                    st.width = saved.width;
                    st.height = saved.height;
                    st.textOverride = saved.textOverride;
                }
            }
        }
    }

    emitBandBody(target, b, originX, originY) {
        this.runBandScript(b);

        // Stretch first: an smActualHeight memo grows with its text, pushes the
        // objects below it down and the band grows with it (FastReport
        // ShiftMode smAlways). smMaxHeight objects do not grow by their own
        // text: they take the height of the tallest object once stretched.
        const grow = new Map();
        const textCache = new Map();
        for (const o of b.objects) {
            const st = this.state(o);
            if (!st.visible || o.kind !== ObjKind.MEMO) continue;
            let value = this.resolveText(o, st);
            textCache.set(o, value);
            if (o.allowHtmlTags && value.indexOf('<') >= 0) value = parseHtmlText(value).text;
            if (b.stretched && isActualStretch(o)) {
                const needed = textHeightUnits(o, st, value);
                if (needed > st.height) grow.set(o, needed - st.height);
            } else if (isActualStretch(o) && !b.stretched) {
                const needed = textHeightUnits(o, st, value);
                if (needed > st.height + 0.5) {
                    this.warn('memo ' + o.name + ' needs more height but band ' + b.name + ' is not stretched');
                }
            }
        }

        // Where every visible object ends up (fr3 units from the band top): the
        // stretched content bottom is what smMaxHeight objects reach to.
        const shiftOf = new Map();
        let contentBottom = b.height;
        for (const o of b.objects) {
            const st = this.state(o);
            if (!st.visible) continue;
            let shift = 0;
            grow.forEach((value, key) => {
                const s = this.state(key);
                if (key !== o && st.top >= s.top + s.height - 0.5) shift = Math.max(shift, value);
            });
            shiftOf.set(o, shift);
            const grown = grow.has(o) ? grow.get(o) : 0;
            contentBottom = Math.max(contentBottom, st.top + shift + st.height + grown);
        }

        let bandHeight = b.height * UNITS_TO_POINTS;
        for (const o of b.objects) {
            const st = this.state(o);
            if (!st.visible) continue;

            const shift = shiftOf.get(o);
            let extra = grow.has(o) ? grow.get(o) : 0;
            if (b.stretched && isMaxStretch(o)) {
                extra = Math.max(extra, contentBottom - (st.top + shift) - st.height);
            }

            const x = originX + (b.left + st.left) * UNITS_TO_POINTS;
            const y = originY + (st.top + shift) * UNITS_TO_POINTS;
            const w = st.width * UNITS_TO_POINTS;
            const h = (st.height + extra) * UNITS_TO_POINTS;

            if (o.color != null && o.kind !== ObjKind.LINE) {
                const fill = fillOp(x, y, w, h, o.color);
                fill.source = o.name;
                target.ops.push(fill);
            }

            switch (o.kind) {
                case ObjKind.PICTURE:
                    this.emitPicture(target, o, st, x, y, w, h);
                    break;
                case ObjKind.MEMO:
                    this.emitMemo(target, o, textCache.has(o) ? textCache.get(o) : this.resolveText(o, st), x, y, w, h);
                    break;
                case ObjKind.BARCODE:
                    this.emitBarcode(target, o, x, y, w, h);
                    break;
                case ObjKind.LINE:
                    this.emitLine(target, o, x, y, w, h);
                    break;
                case ObjKind.SHAPE:
                    this.emitShape(target, o, x, y, w, h);
                    break;
                default:
                    break;
            }

            if (o.frameTyp !== 0 && o.kind !== ObjKind.LINE && o.kind !== ObjKind.SHAPE) {
                const frame = frameOp(x, y, w, h, o.frameTyp, o.frameColor, o.frameWidth);
                frame.source = o.name;
                target.ops.push(frame);
            }
            bandHeight = Math.max(bandHeight, (y - originY) + h);
        }
        return bandHeight;
    }

    /** A band prints once per row: report each distinct issue once. */
    warn(message) {
        if (!this.warnings.includes(message)) this.warnings.push(message);
    }

    state(o) {
        let st = this.objState.get(o.name);
        if (st == null) {
            st = newObjState(o);
            this.objState.set(o.name, st);
        }
        return st;
    }

    emitMemo(target, o, value, x, y, w, h) {
        if (value == null || value === '') return;
        let bold = isBold(o);
        let italic = isItalic(o);
        let underline = isUnderline(o);
        if (o.allowHtmlTags && value.indexOf('<') >= 0) {
            const ht = parseHtmlText(value);
            value = ht.text;
            // One op carries one style: a tag that covers all visible text
            // styles the op; a partial one is dropped.
            bold = bold || ht.allBold;
            italic = italic || ht.allItalic;
            underline = underline || ht.allUnderline;
            if (value === '') return;
        }
        const op = newDrawOp();
        op.kind = DrawKind.TEXT;
        op.x = x;
        op.y = y;
        op.w = w;
        op.h = h;
        op.text = value;
        op.fontName = o.fontName;
        op.fontSize = fontSizePoints(o);
        op.bold = bold;
        op.italic = italic;
        op.underline = underline;
        op.textColor = o.fontColor;
        op.hAlign = o.hAlign;
        op.vAlign = o.vAlign;
        op.wordWrap = o.wordWrap;
        op.source = o.name;
        target.ops.push(op);
    }

    /**
     * Which bytes a picture shows, in FastReport's precedence: a bound image
     * column; the client logo for the picture the script loads from file (or
     * one simply named Logo); the picture embedded in the template; and, for
     * an unbound empty picture, the client logo.
     */
    emitPicture(target, o, st, x, y, w, h) {
        let img;
        const isLogo = st.loadLogo || o.name.toLowerCase() === 'logo';
        if (o.dataField != null && o.dataField !== '') {
            const v = this.options.images.get(o.dataField.toLowerCase());
            img = v === undefined ? null : v;
        } else if (isLogo && this.options.logo != null) {
            img = this.options.logo;
        } else if (o.embeddedImage != null) {
            img = o.embeddedImage;
        } else {
            img = this.options.logo;
        }
        if (img != null) {
            const op = imageOp(x, y, w, h, img, o.center);
            op.source = o.name;
            target.ops.push(op);
        }
    }

    /** Draws the bars as FILL ops in fr3 module units (Zoom), text underneath like FastReport. */
    emitBarcode(target, o, x, y, w, h) {
        const value = this.barcodeValue(o);
        if (!Fr3Barcode.supports(o.barType)) {
            this.warn('barcode ' + o.name + ' type ' + o.barType + ' not rendered');
            return;
        }
        if (o.rotation !== 0) {
            this.warn('barcode ' + o.name + ' rotated ' + o.rotation + ' not rendered');
            return;
        }
        const code = Fr3Barcode.encode(o.barType, value, o.calcCheckSum);
        if (code == null) {
            if (value !== '') this.warn('barcode ' + o.name + ' cannot encode \'' + value + '\'');
            return;
        }
        const unit = UNITS_TO_POINTS;
        let module = Math.max(0.1, o.zoom) * unit;
        const fontPt = fontSizePoints(o);
        const textH = o.showText ? fontPt * LINE_HEIGHT : 0;
        const leadW = o.showText && code.leadingDigit !== '' ? fontPt * 0.9 : 0;
        let total = Fr3Barcode.totalModules(code) * module;
        // Never spill outside the object box.
        if (leadW + total > w && total > 0) {
            module *= Math.max(0.1, (w - leadW) / total);
            total = Fr3Barcode.totalModules(code) * module;
        }
        const barTop = y;
        const barH = Math.max(1, h - textH);
        const guardH = code.ean ? Math.min(h, barH + textH / 2) : barH;
        const bx = x + leadW;

        let moduleIndex = 0;
        let bar = true;
        for (const width of code.widths) {
            if (bar) {
                let guard = false;
                for (const g of code.guards) {
                    if (moduleIndex >= g[0] && moduleIndex < g[0] + g[1]) {
                        guard = true;
                        break;
                    }
                }
                const op = fillOp(bx + moduleIndex * module, barTop, width * module,
                    guard ? guardH : barH, 0x000000);
                op.source = o.name;
                target.ops.push(op);
            }
            moduleIndex += width;
            bar = !bar;
        }

        if (!o.showText) return;
        const textY = barTop + barH;
        if (code.ean) {
            if (code.leadingDigit !== '') {
                target.ops.push(this.barcodeText(o, code.leadingDigit, x, textY, leadW, textH, HAlign.LEFT));
            }
            // Left half sits between the start and middle guards, right half
            // between the middle and end guards.
            const startEnd = code.guards[0][0] + code.guards[0][1];
            const midStart = code.guards[1][0];
            const midEnd = midStart + code.guards[1][1];
            const endStart = code.guards[2][0];
            target.ops.push(this.barcodeText(o, code.leftHalf, bx + startEnd * module, textY,
                (midStart - startEnd) * module, textH, HAlign.CENTER));
            target.ops.push(this.barcodeText(o, code.rightHalf, bx + midEnd * module, textY,
                (endStart - midEnd) * module, textH, HAlign.CENTER));
        } else {
            target.ops.push(this.barcodeText(o, code.text, bx, textY, total, textH, HAlign.CENTER));
        }
    }

    barcodeText(o, text, x, y, w, h, align) {
        const op = newDrawOp();
        op.kind = DrawKind.TEXT;
        op.x = x;
        op.y = y;
        op.w = w;
        op.h = h;
        op.text = text;
        op.fontName = o.fontName;
        op.fontSize = fontSizePoints(o);
        op.bold = isBold(o);
        op.textColor = 0x000000;
        op.hAlign = align;
        op.vAlign = VAlign.TOP;
        op.wordWrap = false;
        op.source = o.name;
        return op;
    }

    barcodeValue(o) {
        let raw = '';
        if (o.expression != null && javaTrim(o.expression) !== '') {
            try {
                raw = this.expr.stringify(this.expr.eval(o.expression, this.context));
            } catch (e) {
                this.warn('barcode ' + o.name + ' expression failed: ' + e.message);
            }
        } else if (o.dataField != null && o.dataField !== '') {
            const f = this.data.field(this.currentRow, o.dataField);
            raw = f == null ? '' : f;
        } else if (o.text != null) {
            raw = o.text.indexOf('[') >= 0 ? this.expr.evalMemoText(o.text, this.context) : o.text;
        }
        return raw == null ? '' : javaTrim(raw);
    }

    /** A horizontal or vertical line as a thin FILL; diagonals are reported. */
    emitLine(target, o, x, y, w, h) {
        const pen = Math.max(0.1, o.frameWidth * UNITS_TO_POINTS);
        let op;
        if (o.diagonal && Math.abs(w) > 0.01 && Math.abs(h) > 0.01) {
            this.warn('diagonal line ' + o.name + ' not rendered');
            return;
        }
        if (Math.abs(h) <= Math.abs(w)) {
            op = fillOp(x, y - pen / 2, w, pen, o.frameColor);
        } else {
            op = fillOp(x - pen / 2, y, pen, h, o.frameColor);
        }
        op.source = o.name;
        target.ops.push(op);
    }

    /** Rectangles (also rounded) as a frame; the background was filled by the caller. */
    emitShape(target, o, x, y, w, h) {
        if (o.shape !== 'skRectangle' && o.shape !== 'skRoundRectangle') {
            this.warn('shape ' + o.name + ' (' + o.shape + ') drawn as a rectangle');
        }
        const op = frameOp(x, y, w, h, FRAME_LEFT | FRAME_RIGHT | FRAME_TOP | FRAME_BOTTOM,
            o.frameColor, o.frameWidth);
        op.source = o.name;
        target.ops.push(op);
    }

    /**
     * Runs the band's OnBeforePrint. Like FastReport, objects are singletons:
     * what a script writes (Text, Visible, Top...) stays on the object until a
     * script writes it again, also across bands. The size-matrix reports
     * (sf_1200 family) depend on it: the zero-height master data band fills
     * the cells of a group footer row by row, the footer prints them and a
     * group header clears them. State starts from the design values once per
     * layout pass (layout()).
     */
    runBandScript(b) {
        if (b.onBeforePrint == null || b.onBeforePrint === '') return;
        if (!this.script.hasProcedure(b.onBeforePrint)) return;
        try {
            this.script.run(b.onBeforePrint, this.scriptVars, this.access, this.expr, this.context);
        } catch (e) {
            this.warn('script ' + b.onBeforePrint + ' failed: ' + e.message);
        }
    }

    resolveText(o, st) {
        if (st.textOverride != null) return st.textOverride;
        const raw = o.text == null ? '' : o.text;
        let value;
        if (raw.indexOf('[') >= 0) {
            value = this.expr.evalMemoText(raw, this.context);
        } else if (o.dataField != null && o.dataField !== '') {
            const f = this.data.field(this.currentRow, o.dataField);
            value = f == null ? '' : f;
        } else {
            return raw;
        }
        // A bracketed memo is a single bound value, so display formatting
        // applies to it as a whole; literal text passes through untouched.
        if (o.formatStr != null && o.formatStr !== '') {
            let f = this.format;
            if (o.decimalSeparator != null && o.decimalSeparator.length === 1) {
                f = new Fr3Format();
                f.decimalSeparator = o.decimalSeparator;
                f.thousandSeparator = this.format.thousandSeparator;
            }
            return f.apply(value, o.formatKind, o.formatStr, o.hideZeros);
        }
        if (o.hideZeros) {
            const d = Fr3Format.toDouble(value);
            if (d != null && d === 0) return '';
        }
        return this.format.autoFormatDate(value);
    }

    band(page, kind) {
        for (const b of page.bands) {
            if (b.kind === kind) return b;
        }
        return null;
    }

    /** All bands of a kind, in designer order (outermost group first). */
    bands(page, kind) {
        return page.bands.filter((b) => b.kind === kind);
    }

    /**
     * Group footers nest the other way round: the one closest to the master
     * data closes the innermost group. With fewer footers than headers the
     * innermost groups get them first.
     */
    groupFootersByLevel(page, levels) {
        const asc = this.bands(page, BandKind.GROUP_FOOTER);
        asc.sort((a, b) => (a.top < b.top ? -1 : (a.top > b.top ? 1 : 0)));
        const out = [];
        for (let level = 0; level < levels; level++) {
            const idx = levels - 1 - level;
            out.push(idx < asc.length ? asc[idx] : null);
        }
        return out;
    }

    /**
     * Splits rows [from, to] on the group condition: a field reference as a
     * rule, any other expression evaluated per row.
     */
    groupRanges(groupHeader, from, to) {
        const out = [];
        if (to < from) {
            out.push([from, to]);
            return out;
        }
        const field = groupHeader == null ? '' : fieldName(groupHeader.condition);
        const byField = field !== '' && this.data.hasField(field);
        const cond = groupHeader == null ? '' : groupHeader.condition;
        if (!byField && (cond == null || javaTrim(cond) === '')) {
            out.push([from, to]);
            return out;
        }
        let start = from;
        let prev = this.groupKey(byField, field, cond, from);
        for (let i = from + 1; i <= to; i++) {
            const v = this.groupKey(byField, field, cond, i);
            const same = prev == null ? v == null : prev === v;
            if (!same) {
                out.push([start, i - 1]);
                start = i;
                prev = v;
            }
        }
        out.push([start, to]);
        return out;
    }

    groupKey(byField, field, cond, row) {
        if (byField) return this.data.field(row, field);
        const save = this.currentRow;
        this.currentRow = row;
        try {
            return this.expr.stringify(this.expr.eval(cond, this.context));
        } catch (e) {
            return '';
        } finally {
            this.currentRow = save;
        }
    }

    // -- evaluation wiring ---------------------------------------------------------

    makeContext() {
        const self = this;
        return {
            field(name) {
                return self.data.field(self.currentRow, name);
            },
            sumField(name) {
                let sum = 0;
                const from = Math.max(0, self.groupStart);
                const to = Math.min(self.groupEnd, self.data.rowCount() - 1);
                for (let i = from; i <= to; i++) {
                    const d = Fr3Format.toDouble(self.data.field(i, name));
                    if (d != null) sum += d;
                }
                return sum;
            },
            scopeFrom() { return Math.max(0, self.groupStart); },
            scopeTo() { return Math.min(self.groupEnd, self.data.rowCount() - 1); },
            currentRow() { return self.currentRow; },
            moveTo(row) { self.currentRow = row; },
            variable(name) {
                const key = name.toLowerCase();
                // FastReport system variables.
                if (key === 'page#' || key === 'page') return Math.max(1, self.currentPageNo);
                if (key === 'totalpages#') {
                    return self.totalPages > 0 ? self.totalPages : Math.max(1, self.currentPageNo);
                }
                if (key === 'line#') return Math.max(1, self.lineNo);
                if (key === 'date') return formatDateFields('dd/MM/yyyy', fieldsOf(self.now()));
                if (key === 'time') return formatDateFields('HH:mm:ss', fieldsOf(self.now()));
                if (self.scriptVars.has(key)) return self.scriptVars.get(key);
                const vars = self.options.variables;
                if (vars.has(name)) return vars.get(name);
                for (const [k, v] of vars) {
                    if (k.toLowerCase() === name.toLowerCase()) return v;
                }
                if (self.data.hasVariable(name)) return self.data.variable(name);
                // MySales / MyFashionManager always set these before printing
                // (KasMainForm PrintSQL :34178, MainUnit :8207); the template's
                // stored values are the design-time ones of whoever saved it.
                if (isHostVariable(name)) return '';
                return self.templateVariable(name);
            },
        };
    }

    now() {
        return this.options.now != null ? this.options.now : new Date();
    }

    /**
     * A variable's design-time value from the template's Variables list, e.g.
     * RepTitle = '1200. Voorraad per maat'. Evaluated once.
     */
    templateVariable(name) {
        let def = null;
        for (const [k, v] of this.doc.variables) {
            if (k.toLowerCase() === name.toLowerCase()) {
                def = v;
                break;
            }
        }
        if (def == null) return null;
        const key = name.toLowerCase();
        if (this.docVarCache.has(key)) return this.docVarCache.get(key);
        let v;
        // Guard against a variable whose value refers to itself.
        this.docVarCache.set(key, '');
        try {
            v = javaTrim(def) === '' ? '' : this.expr.eval(def, this.context);
        } catch (e) {
            v = def;
        }
        this.docVarCache.set(key, v);
        return v;
    }

    makeAccess() {
        const self = this;
        return {
            isObject(name) {
                return name != null && (self.objState.has(name) || self.objByName.has(name));
            },
            getProperty(objectName, property) {
                let st = self.objState.get(objectName);
                if (st == null) {
                    const o = self.objByName.get(objectName);
                    if (o == null) return null;
                    st = newObjState(o);
                }
                switch (property.toLowerCase()) {
                    case 'top': return st.top;
                    case 'left': return st.left;
                    case 'width': return st.width;
                    case 'height': return st.height;
                    case 'visible': return st.visible;
                    case 'text': return st.textOverride == null ? '' : st.textOverride;
                    default: return null;
                }
            },
            setProperty(objectName, property, value) {
                let st = self.objState.get(objectName);
                if (st == null) {
                    const o = self.objByName.get(objectName);
                    if (o == null) return;
                    st = newObjState(o);
                    self.objState.set(objectName, st);
                }
                switch (property.toLowerCase()) {
                    case 'top': st.top = exprNum(value); break;
                    case 'left': st.left = exprNum(value); break;
                    case 'width': st.width = exprNum(value); break;
                    case 'height': st.height = exprNum(value); break;
                    case 'visible': st.visible = exprTruth(value); break;
                    case 'text': st.textOverride = value == null ? '' : self.expr.stringify(value); break;
                    default: break;
                }
            },
            invoke(objectName, method, args) {
                // Set('RepTitle', value): assigns a report variable, no object.
                if (objectName == null && method.toLowerCase() === 'set' && args.length >= 2) {
                    self.options.variables.set(self.expr.stringify(args[0]), self.expr.stringify(args[1]));
                    return;
                }
                if (method.toLowerCase() === 'loadfromfile') {
                    let st = self.objState.get(objectName);
                    if (st == null) {
                        const o = self.objByName.get(objectName);
                        if (o != null) {
                            st = newObjState(o);
                            self.objState.set(objectName, st);
                        }
                    }
                    if (st != null) st.loadLogo = true;
                    return;
                }
                self.warn('unsupported script call ' + objectName + '.' + method);
            },
        };
    }
}

/** Cursor over the flowing area of the current page. */
class Cursor {
    constructor(engine, pages, page, left, top, flowBottom, pageHeader, columnHeader) {
        this.e = engine;
        this.pages = pages;
        this.page = page;
        this.left = left;
        this.top = top;
        this.flowBottom = flowBottom;
        this.pageHeader = pageHeader;
        this.columnHeader = columnHeader;
        /** Header / group headers with ReprintOnNewPage that are currently open. */
        this.reprint = [];
        /** State at the moment each page was closed, for its page footer. */
        this.pageStates = [];
        this.current = null;
        this.y = 0;
        /**
         * A flowing band (data, group header/footer, summary...) was placed on
         * the current page. Page header, column header, report title and the
         * reprinted headers do not count: StartNewPage and the overflow break
         * only start a new page when something has actually flowed onto this
         * one - comparing y to the design height of the page header gave an
         * empty first page as soon as the header grew.
         */
        this.flowed = false;
    }

    /** First page: report title before the page header (TitleBeforeHeader). */
    start(title) {
        this.openPage();
        if (title != null && title.visible) this.emitWithChildren(title);
        this.pageTop();
    }

    openPage() {
        if (this.current != null) this.pageStates.push(this.e.capturePage());
        if (this.pages.length >= this.e.options.maxPages) {
            throw new Error('template exceeded ' + this.e.options.maxPages + ' pages');
        }
        this.current = {
            widthPoints: pageWidthPoints(this.page),
            heightPoints: pageHeightPoints(this.page),
            ops: [],
        };
        this.pages.push(this.current);
        this.e.currentPageNo = this.pages.length;
        this.y = this.top;
        this.flowed = false;
    }

    pageTop() {
        if (this.pageHeader != null && this.pageHeader.visible) this.emitWithChildren(this.pageHeader);
        if (this.columnHeader != null && this.columnHeader.visible) this.emitWithChildren(this.columnHeader);
    }

    newPage() {
        this.openPage();
        this.pageTop();
        // Copy: emitting a reprinted band must not touch the list it is on.
        for (const b of this.reprint.slice()) this.emitWithChildren(b);
    }

    /** StartNewPage: a fresh page unless nothing has flowed onto this one yet. */
    breakPage() {
        if (this.flowed) this.newPage();
    }

    /** Emits a flowing band (and its child chain), breaking the page first when it does not fit. */
    place(b) {
        if (!b.visible) return;
        const needed = this.e.measure(b);
        if (this.y + needed > this.flowBottom && this.flowed) {
            this.newPage();
        }
        this.emitWithChildren(b);
        this.flowed = true;
    }

    emitWithChildren(b) {
        const seen = new Set();
        let cur = b;
        while (cur != null && cur.visible && !seen.has(cur.name)) {
            seen.add(cur.name);
            this.y += this.e.emitBandInto(this.current, cur, this.left, this.y);
            cur = cur.childName == null || cur.childName === '' ? null : (this.e.bandByName.get(cur.childName) || null);
        }
    }
}

/** List.remove(Object): the first occurrence. */
const removeFirst = (list, item) => {
    const i = list.indexOf(item);
    if (i >= 0) list.splice(i, 1);
};

export default Fr3Engine;
